import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArtifactProcessService } from '../../services/api/artifactProcessService';
import { ArtifactProcessCreate } from '../../types/artifactProcess';
import styles from './CreateCompanyProcessDialog.module.css';

interface CreateCompanyProcessDialogProps {
  artifact: {
    id: string;
    name: string;
  } | null;
  onProcessCreated: (processId: string) => void;
  onCancel: () => void;
}

interface ProcessFormData {
  name: string;
  description: string;
}

const CreateCompanyProcessDialog: React.FC<CreateCompanyProcessDialogProps> = ({
  artifact,
  onProcessCreated,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ProcessFormData>({
    name: '',
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      setError('Please fill in the process name');
      return;
    }

    if (!artifact?.id) {
      setError('No artifact selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the company process using the correct field names
      const processData: ArtifactProcessCreate = {
        name: formData.name,
        description: formData.description,
        artifact_ref: artifact.id, // This is the company artifact ID
        process_type: 'MANUAL', // Default process type
        demand_flow_type: 'DRUM', // Default demand flow type
        process_status: 'DRAFT',
        // prime_process_ref is optional - will be set later if needed
      };

      console.log('Creating company process with data:', processData);
      const newProcess = await ArtifactProcessService.create(processData);
      console.log('Created company process:', newProcess);

      // Call the callback to notify parent component
      onProcessCreated(newProcess.id);

      // Navigate to the new process page
      navigate(`/company-processes/${newProcess.id}`);
    } catch (err: any) {
      console.error('Error creating company process:', err);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to create process';
      
      if (err?.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Validation errors
          errorMessage = err.response.data.detail
            .map((error: any) => {
              const field = error.loc?.join('.') || 'field';
              const msg = error.msg || error.message || 'validation error';
              return `${field}: ${msg}`;
            })
            .join('\n');
        } else {
          errorMessage = String(err.response.data.detail);
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProcessFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContent} ${styles.createCompanyProcessDialog}`}>
        <h3>Create Company Process</h3>
        <p>
          Create a new process for <strong>{artifact?.name || 'this artifact'}</strong>
        </p>

        <div className={styles.infoMessage}>
          This will create a basic company process. You can configure states, transitions, and other
          details after creation.
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <form onSubmit={handleSubmit} className={styles.createCompanyProcessForm}>
          <div className="form-group">
            <label htmlFor="process-name">Process Name *</label>
            <input
              id="process-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              placeholder="Enter process name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="process-description">Description</label>
            <textarea
              id="process-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter process description (optional)"
              rows={3}
            />
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.createButton} disabled={loading || !formData.name}>
              {loading ? 'Creating...' : 'Create Process'}
            </button>
            <button type="button" onClick={onCancel} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompanyProcessDialog;
